import { Add } from "@mui/icons-material";
import { Button, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { useState, useRef, useEffect } from "react";
import { useMedicalCareContext } from "../../../../contexts/MedicalCareContext";
import { useWatch } from "react-hook-form";
import InputText from "@/components/form/InputText";
import { toast } from "react-toastify";

export function AddConduta() {
  const { control, unregister, condutas, setCondutas, setValue } =
    useMedicalCareContext();
  const [currentIndex, setCurrentIndex] = useState<number>(
    condutas?.length || 0
  );
  // Referência para armazenar o timeout atual
  const timeoutRef = useRef<any>(null);
  // Estado para armazenar o valor atual sendo digitado
  const [currentConduta, setCurrentConduta] = useState<string>("");

  const formValues = useWatch({
    name: "New_Conduta",
    control,
  }) as string[];

  // Função para verificar duplicatas durante a digitação
  const checkDuplicatesWithDelay = (value: string) => {
    // Limpa qualquer timeout existente
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (!value || value.trim() === "") return;

    // Verifica se a conduta já existe na lista
    const existsInCurrentList = condutas.some(
      (conduta: string) => conduta.trim() === value.trim()
    );

    // Verifica se já existe nas condutas abertas
    const flatOpenItens = document.querySelectorAll(
      ".lista-condutas-abertas .MuiTypography-root"
    );
    const existsInOpenList = Array.from(flatOpenItens).some(
      (item: Element) => item.textContent?.trim() === value.trim()
    );

    if (existsInCurrentList || existsInOpenList) {
      // Configura um timeout para limpar após 2 segundos
      timeoutRef.current = setTimeout(() => {
        // Se já existe, mostrar um toast de aviso
        toast.warning(`"${value}" já está na lista de condutas`, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        // Abordagem 1: React Hook Form - múltiplas tentativas
        setValue(`New_Conduta.${currentIndex}`, null);
        setValue(`New_Conduta.${currentIndex}`, "");
        setValue(`New_Conduta.${currentIndex}`, undefined);
        unregister(`New_Conduta.${currentIndex}`);

        // Abordagem 2: React State
        setCurrentConduta("");

        // Abordagem 3: DOM direto (mais confiável)
        setTimeout(() => {
          // Seletor específico para textareas
          const textareas = document.querySelectorAll("textarea");
          textareas.forEach((textarea: any) => {
            textarea.value = "";
          });

          // Garantindo que o evento change seja disparado
          const event = new Event("change", { bubbles: true });
          textareas.forEach((textarea: any) => {
            textarea.dispatchEvent(event);
          });

          // Um último setValue para garantir
          setValue(`New_Conduta.${currentIndex}`, null);
          setValue(`New_Conduta.${currentIndex}`, "");
          unregister(`New_Conduta.${currentIndex}`);

          // Resetar o formulário como último recurso
          try {
            const formElement = document.querySelector("form");
            if (formElement) {
              formElement.reset();
            }
          } catch (e) {
            console.error("Erro ao tentar resetar o formulário", e);
          }
        }, 10);
      }, 2000); // 2 segundos de delay
    }
  };

  // Efeito para monitorar mudanças no valor atual da conduta
  useEffect(() => {
    if (currentConduta) {
      checkDuplicatesWithDelay(currentConduta);
    }
  }, [currentConduta]);

  const handleAddConduta = () => {
    const currentValue = formValues?.[currentIndex];
    if (currentValue?.trim()) {
      // Verifica se a conduta já existe na lista de condutas
      const existsInCurrentList = condutas.some(
        (conduta: string) => conduta === currentValue
      );

      // Verifica se já existe nas condutas abertas
      const flatOpenItens = document.querySelectorAll(
        ".lista-condutas-abertas .MuiTypography-root"
      );
      const existsInOpenList = Array.from(flatOpenItens).some(
        (item: Element) => item.textContent?.trim() === currentValue.trim()
      );

      if (existsInCurrentList || existsInOpenList) {
        // Se já existe, mostrar um toast de aviso
        toast.warning(`"${currentValue}" já está na lista de condutas`, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        // Limpar campo completamente - abordagem mais direta
        setValue(`New_Conduta.${currentIndex}`, "");
        unregister(`New_Conduta.${currentIndex}`);

        // Forçar limpeza direta no DOM
        setTimeout(() => {
          // Limpa diretamente via DOM para garantir
          const formElement = document.querySelector("form");
          if (formElement) {
            const textareas = formElement.querySelectorAll("textarea");
            textareas.forEach((textarea: any) => {
              if (textarea.value.includes(currentValue)) {
                textarea.value = "";
              }
            });
          }

          // Tenta várias abordagens para garantir a limpeza
          setValue(`New_Conduta.${currentIndex}`, null);
          setValue(`New_Conduta.${currentIndex}`, "");
          setValue(`New_Conduta.${currentIndex}`, undefined);
          unregister(`New_Conduta.${currentIndex}`);
          control.unregister(`New_Conduta.${currentIndex}`);
        }, 50);
      } else {
        // Se não existe, adicionar normalmente
        setCondutas((prev: string[]) => [...prev, currentValue]);
        setCurrentIndex((prev: number) => prev + 1);
      }
    }
  };

  return (
    <Stack spacing={2}>
      {condutas?.length > 0 && (
        <Stack
          direction="row"
          flexWrap="wrap"
          spacing={1}
          paddingBottom={1}
          gap={1}
        >
          {condutas.map(
            (conduta: string, index: number) =>
              conduta?.length > 0 && (
                <Chip
                  key={index}
                  sx={{ padding: "8px", height: "auto" }}
                  label={
                    conduta.length > 85 ? (
                      <Tooltip
                        placement="right"
                        title={conduta}
                        slotProps={{
                          popper: {
                            modifiers: [
                              {
                                name: "offset",
                                options: { offset: [80, 20] },
                              },
                            ],
                          },
                        }}
                      >
                        <Typography>{conduta.substring(0, 85)}...</Typography>
                      </Tooltip>
                    ) : (
                      <Typography>{conduta}</Typography>
                    )
                  }
                  onDelete={() => {
                    setCondutas((prev: string[]) => {
                      const updated = [...prev];
                      updated[index] = "";
                      return updated;
                    });
                    unregister(`New_Conduta.${index}`);
                  }}
                />
              )
          )}
        </Stack>
      )}

      <Stack spacing={1}>
        <InputText
          key={`conduta-input-${currentIndex}`}
          label="Conduta"
          name={`New_Conduta.${currentIndex}`}
          control={control}
          sx={{ mb: 4 }}
          onKeyDown={(e: React.KeyboardEvent) =>
            e.key === "Enter" && (e.preventDefault(), handleAddConduta())
          }
          handleChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setCurrentConduta(e.target.value)
          }
          minRows={3}
          maxRows={100}
          autoFocus
          multiline
        />

        <Stack direction="row" justifyContent="flex-end">
          <Button
            sx={{ width: "fit-content" }}
            onClick={handleAddConduta}
            variant="text"
          >
            <Add />
            Adicionar
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
}
